import { useEffect, useCallback } from 'react';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
  action: () => void;
  description: string;
  preventDefault?: boolean;
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  target?: 'document' | 'window';
}

export const useKeyboardShortcuts = (
  shortcuts: KeyboardShortcut[], 
  options: UseKeyboardShortcutsOptions = {}
) => {
  const { enabled = true, target = 'document' } = options;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when user is typing in input fields
    const activeElement = document.activeElement;
    const isTypingInInput = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.getAttribute('contenteditable') === 'true'
    );

    if (isTypingInInput && !event.altKey && !event.ctrlKey && !event.metaKey) {
      return;
    }

    for (const shortcut of shortcuts) {
      const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatches = event.ctrlKey === !!shortcut.ctrlKey;
      const altMatches = event.altKey === !!shortcut.altKey;
      const shiftMatches = event.shiftKey === !!shortcut.shiftKey;
      const metaMatches = event.metaKey === !!shortcut.metaKey;

      if (keyMatches && ctrlMatches && altMatches && shiftMatches && metaMatches) {
        if (shortcut.preventDefault !== false) {
          event.preventDefault();
        }
        shortcut.action();
        break;
      }
    }
  }, [enabled, shortcuts]);

  useEffect(() => {
    const targetElement = target === 'window' ? window : document;
    
    targetElement.addEventListener('keydown', handleKeyDown as EventListener);
    
    return () => {
      targetElement.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }, [handleKeyDown, target]);

  // Helper function to get shortcut descriptions for help/documentation
  const getShortcutDescriptions = useCallback(() => {
    return shortcuts.map(shortcut => {
      let keyCombo = '';
      if (shortcut.ctrlKey) keyCombo += 'Ctrl + ';
      if (shortcut.altKey) keyCombo += 'Alt + ';
      if (shortcut.shiftKey) keyCombo += 'Shift + ';
      if (shortcut.metaKey) keyCombo += 'Cmd + ';
      keyCombo += shortcut.key.toUpperCase();
      
      return {
        keys: keyCombo,
        description: shortcut.description
      };
    });
  }, [shortcuts]);

  return { getShortcutDescriptions };
};

// Common keyboard shortcuts for the application
export const createNavigationShortcuts = (
  focusNavigation: () => void,
  focusMainContent: () => void,
  focusSearch?: () => void
): KeyboardShortcut[] => [
  {
    key: 'n',
    altKey: true,
    action: focusNavigation,
    description: 'Focus main navigation'
  },
  {
    key: 'm',
    altKey: true,
    action: focusMainContent,
    description: 'Focus main content'
  },
  ...(focusSearch ? [{
    key: 's',
    altKey: true,
    action: focusSearch,
    description: 'Focus search'
  }] : [])
];
